import React, { useState, useEffect } from 'react';
import { Container, StyledLabelP } from 'modules/Sty';
import TableElement, { ColDef } from 'elements/TableElement';
import { getUnifiedInvoices_api, UnifiedInvoice, UserInvoiceResponse, InvoiceFilters, formatInvoiceAmount, formatInvoiceDate, getInvoiceTypeDisplay, getInvoiceStatusDisplay } from 'api/invoiceApi';
import useUser, { UserExistsState } from 'Hooks/useUser';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useAppView from 'Hooks/useAppView';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import ButtonSymphony from 'elements/ButtonSymphony';
import Loader from 'elements/Loader';
import dayjs from 'dayjs';
import { PLAYBOOK_TYPE_INFO } from 'modules/Const';
import InvoiceFilterOptions from './InvoiceFilterOptions';
import DropdownArrow from 'components/shareable/DropdownArrow';
import Colors from 'modules/Colors';

interface InvoiceFilter {
    brands: string[];
    statuses: string[];
    dateModifier: 'all_time' | 'in_between' | 'before' | 'after' | 'last_days';
    startDate: string;
    endDate: string;
    filterDays: string;
    amountModifier: 'no_amount_filter' | 'equal' | 'greater_than' | 'less_than' | 'between_two_numbers';
    amountValue1: string;
    amountValue2: string;
}

interface ComponentInvoice {
    id: string;
    amount: number;
    currency: string;
    status: string;
    dueDate: string;
    createdDate: string;
    invoiceLink?: string | null;

    // Entity information
    entityType: 'organization' | 'brand';
    entityName: string;
    organizationName?: string;

    // Type and categorization
    type: 'subscription' | 'campaign' | 'task-pack' | 'mixed';
    description: string;

    // Campaign-specific data (if applicable)
    campaignName?: string;
    campaignPlaybook?: string;

    // For table display
    date: string;
    amountPaid: number;
    paymentTypeDisplay: string;
    note: string;
    brandName: string;
}

const InvoicesTabV2: React.FC = () => {
    const { currentUser } = useUser() as UserExistsState;
    const { currentBrand } = useBrand() as BrandExistsState;
    const { isTeamView, appView } = useAppView();
    const { organization } = useOrganizationV2();

    const [invoices, setInvoices] = useState<ComponentInvoice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<UserInvoiceResponse['summary'] | null>(null);
    const [isShowFilters, setIsShowFilters] = useState(false);
    const [isShowFilterItems, setIsShowFilterItems] = useState(false);

    // Initialize filter with default values
    const [filter, setFilter] = useState<InvoiceFilter>({
        brands: [],
        statuses: [],
        dateModifier: 'all_time',
        startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        filterDays: '30',
        amountModifier: 'no_amount_filter',
        amountValue1: '',
        amountValue2: '',
    });

    const loadInvoices = async (appliedFilter?: InvoiceFilter) => {
        if (!currentUser) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Build API filters from component filter
            const activeFilter = appliedFilter || filter;
            const apiFilters: InvoiceFilters = {};

            // Apply brand filters from UI first
            if (activeFilter.brands.length > 0) {
                const brandId = parseInt(activeFilter.brands[0]);
                if (!isNaN(brandId)) {
                    apiFilters.brandId = brandId;
                }
            }
            // Auto-scope based on current view context (only if no brand filter applied)
            else if (isTeamView() && organization?.id) {
                // In team view without brand filter, show invoices for the current organization
                apiFilters.organizationId = organization.id;
            } else if (!isTeamView() && currentBrand?.id) {
                // In brand view, show invoices for the current brand
                apiFilters.brandId = currentBrand.id;
            }

            // Date filters
            if (activeFilter.dateModifier !== 'all_time') {
                if (activeFilter.dateModifier === 'in_between') {
                    apiFilters.startDate = activeFilter.startDate;
                    apiFilters.endDate = activeFilter.endDate;
                } else if (activeFilter.dateModifier === 'after') {
                    apiFilters.startDate = activeFilter.startDate;
                } else if (activeFilter.dateModifier === 'before') {
                    apiFilters.endDate = activeFilter.startDate;
                } else if (activeFilter.dateModifier === 'last_days') {
                    const days = parseInt(activeFilter.filterDays) || 30;
                    apiFilters.startDate = dayjs().subtract(days, 'days').format('YYYY-MM-DD');
                    apiFilters.endDate = dayjs().format('YYYY-MM-DD');
                }
            }

            // Amount filters
            if (activeFilter.amountModifier !== 'no_amount_filter') {
                const amount1 = parseFloat(activeFilter.amountValue1) || 0;
                const amount2 = parseFloat(activeFilter.amountValue2) || 0;

                switch (activeFilter.amountModifier) {
                    case 'equal':
                        apiFilters.minAmount = amount1;
                        apiFilters.maxAmount = amount1;
                        break; 
                    case 'greater_than':
                        apiFilters.minAmount = amount1;
                        break;
                    case 'less_than':
                        apiFilters.maxAmount = amount1;
                        break;
                    case 'between_two_numbers':
                        apiFilters.minAmount = Math.min(amount1, amount2);
                        apiFilters.maxAmount = Math.max(amount1, amount2);
                        break;
                }
            }

            console.log('Sending API filters:', apiFilters);
            const response: UserInvoiceResponse = await getUnifiedInvoices_api(apiFilters);

            // Transform unified invoices to component format
            let formattedInvoices: ComponentInvoice[] = response.invoices.map(inv => {
                let entityDisplayName = inv.entityName;
                if (inv.entityType === 'brand' && inv.organizationName) {
                    entityDisplayName = `${inv.entityName} (${inv.organizationName})`;
                }

                // Generate payment type display
                let paymentTypeDisplay: string;
                paymentTypeDisplay = inv.type.charAt(0).toUpperCase() + inv.type.slice(1);


                // Generate note content
                let note = inv.description || '';
                if (inv.type === 'campaign') {
                    if (inv.campaignName) {
                        const playbookInfo = PLAYBOOK_TYPE_INFO[inv.campaignPlaybook as keyof typeof PLAYBOOK_TYPE_INFO];
                        const playbookName = playbookInfo?.title || inv.campaignPlaybook?.replace('_', ' ') || 'Campaign';
                        note = `${inv.campaignName} (${playbookName})`;
                    } else if (inv.campaignPlaybook) {
                        const playbookInfo = PLAYBOOK_TYPE_INFO[inv.campaignPlaybook as keyof typeof PLAYBOOK_TYPE_INFO];
                        note = playbookInfo?.title || inv.campaignPlaybook.replace('_', ' ');
                    }
                } else if (inv.type === 'subscription') {
                    note = inv.description || 'Subscription Payment';
                } else if (inv.type === 'task-pack') {
                    note = inv.description || 'Task Pack Purchase';
                } else {
                    note = inv.description || inv.type;
                }

                return {
                    id: inv.id,
                    amount: inv.amount,
                    currency: inv.currency,
                    status: inv.status,
                    dueDate: inv.dueDate || 'N/A',
                    createdDate: inv.createdDate,
                    invoiceLink: inv.invoiceLink,

                    // Entity information
                    entityType: inv.entityType,
                    entityName: inv.entityName,
                    organizationName: inv.organizationName,

                    // Type and categorization
                    type: inv.type,
                    description: inv.description,

                    // Campaign-specific data
                    campaignName: inv.campaignName,
                    campaignPlaybook: inv.campaignPlaybook,

                    // For table display
                    date: dayjs(inv.createdDate).format('MMM DD, YYYY'),
                    amountPaid: inv.amount,
                    paymentTypeDisplay: paymentTypeDisplay,
                    note: note,
                    brandName: entityDisplayName,
                };
            });

            // Apply client-side status filtering (backend returns all paid invoices)
            if (activeFilter.statuses.length > 0) {
                formattedInvoices = formattedInvoices.filter(inv =>
                    activeFilter.statuses.includes(inv.status.toLowerCase())
                );
            }

            setInvoices(formattedInvoices);
            setSummary(response.summary);
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            setError('Failed to load invoices. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadInvoices();
    }, [organization, currentBrand, appView]);

    const handleFilterChange = (newFilter: InvoiceFilter) => {
        setFilter(newFilter);
    };

    const handleApplyFilters = () => {
        loadInvoices(filter);
    };

    const resetFilters = () => {
        const defaultFilter: InvoiceFilter = {
            brands: [],
            statuses: [],
            dateModifier: 'all_time',
            startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            filterDays: '30',
            amountModifier: 'no_amount_filter',
            amountValue1: '',
            amountValue2: '',
        };
        setFilter(defaultFilter);
        loadInvoices(defaultFilter);
    };

    const hasInvoices = invoices.length > 0;
    const hasFiltersApplied = filter.brands.length > 0 ||
        filter.statuses.length > 0 ||
        filter.dateModifier !== 'all_time' ||
        filter.amountModifier !== 'no_amount_filter';

    // Always show filters - users should be able to filter even when no results
    React.useEffect(() => {
        setIsShowFilters(true);
    }, []);

    // Determine context for display
    const getContextTitle = () => {
        if (isTeamView() && organization?.name) {
            return `Invoices - ${organization.name}`;
        } else if (!isTeamView() && currentBrand?.name) {
            return `Invoices - ${currentBrand.name}`;
        }
        return 'Invoices';
    };

    const renderFilterButton = () => {
        return (
            <ButtonSymphony
                className="filtersInvoiceToggleButton"
                variant="outlined"
                onClick={() => setIsShowFilterItems(!isShowFilterItems)}
                style={{
                    paddingLeft: 18,
                    paddingRight: 14,
                }}
                iconRight={
                    <DropdownArrow
                        color={Colors.purple}
                        width={16}
                        height={16}
                        direction={isShowFilterItems ? 'up' : 'down'}
                    />
                }
            >
                Filters
            </ButtonSymphony>
        );
    };

    const renderFilters = () => {
        if (isShowFilters && isShowFilterItems) {
            return (
                <InvoiceFilterOptions
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={resetFilters}
                    isLoading={isLoading}
                />
            );
        }
        return null;
    };

    if (isLoading) {
        return (
            <Container className="invoicesTabContainer" padding="24">
                <StyledLabelP fontSize={18} margin="0 0 16 0">{getContextTitle()}</StyledLabelP>
                <Loader
                    className="invoicesLoader"
                    title="Loading invoices..."
                    size={44}
                    isCentered
                />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="invoicesTabContainer" padding="24">
                <StyledLabelP fontSize={18} margin="0 0 16 0">{getContextTitle()}</StyledLabelP>
                <StyledLabelP color="#EF4444">{error}</StyledLabelP>
                <Container margin="16 0 0 0">
                    <ButtonSymphony
                        className="retryButton"
                        onClick={() => loadInvoices()}
                    >
                        Try Again
                    </ButtonSymphony>
                </Container>
            </Container>
        );
    }

    if (!hasInvoices) {
        return (
            <Container className="invoicesTabContainer" padding="24">
                <StyledLabelP fontSize={18} margin="0 0 16 0">{getContextTitle()}</StyledLabelP>

                <Container className="noInvoicesContainer" padding="48 0" style={{ textAlign: 'center' }}>
                    <StyledLabelP fontSize={18} margin="0 0 8 0">
                        No invoices found
                    </StyledLabelP>
                    <StyledLabelP color="#6B7280" margin="0 0 16 0">
                        {hasFiltersApplied
                            ? "Try adjusting your filters to see more results."
                            : isTeamView() && organization?.name
                                ? `Invoices will appear here as payments are processed for ${organization.name}.`
                                : currentBrand?.name
                                    ? `Invoices will appear here as payments are processed for ${currentBrand.name}.`
                                    : "Invoices will appear here as payments are processed for your campaigns and subscriptions."
                        }
                    </StyledLabelP>
                    {hasFiltersApplied && (
                        <ButtonSymphony
                            className="clearFiltersButton"
                            onClick={resetFilters}
                        >
                            Clear Filters
                        </ButtonSymphony>
                    )}
                </Container>
            </Container>
        );
    }

    // Define table columns
    const tableColumns: ColDef[] = [
        {
            field: 'date',
            title: 'Date',
            minWidth: 120,
            isSortable: true,
        },
        {
            field: 'amountPaid',
            title: 'Amount Paid',
            minWidth: 120,
            isSortable: true,
            renderCell: (row) => formatInvoiceAmount(row.amountPaid, row.currency)
        },
        {
            field: 'paymentTypeDisplay',
            title: 'Payment Type',
            minWidth: 150,
            isSortable: true,
        },
        {
            field: 'note',
            title: 'Note',
            flex: 2,
            minWidth: 200,
        },
        {
            field: 'currency',
            title: 'Currency',
            minWidth: 80,
            isSortable: true,
        },
        {
            field: 'brandName',
            title: 'Brand',
            minWidth: 150,
            isSortable: true,
        },
        {
            field: 'invoiceLink',
            title: 'Invoice',
            minWidth: 100,
            renderCell: (row) => {
                if (row.invoiceLink) {
                    return (
                        <a
                            href={row.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#8B5CF6',
                                textDecoration: 'none',
                                fontWeight: 500,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = 'none';
                            }}
                        >
                            View Invoice
                        </a>
                    );
                }
                return <span style={{ color: '#9CA3AF' }}>—</span>;
            }
        },
    ];

    return (
        <Container className="invoicesTabContainer" padding="24">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <StyledLabelP fontSize={18} margin="0">{getContextTitle()}</StyledLabelP>
                {renderFilterButton()}
            </div>
            
            {renderFilters()}

            <TableElement
                className="invoicesTable"
                rowData={invoices}
                columnDefs={tableColumns}
                pagination={true}
                paginationPageSize={10}
                emptyTableMessage="No invoices found"
            />
        </Container>
    );
};

export default InvoicesTabV2;
